import { useEffect, useState } from 'react';
import type { AddressType } from '@common/enums';
import type { IHomeAddress, IInvoiceAddress } from '@common/validation/costInfoAndConsent';
import type { ValidationErrors } from '@frontend/utils';
import { AddressCountry, PaymentMethod } from '@common/enums';
import { costInfoAndConsentSchema } from '@common/validation/costInfoAndConsent';
import { emptyValidationErrors, useAbortableFetch, validate, validateOne } from '@frontend/utils';

interface IPatientInformation {
    emailAddress?: string | null;
    homeAddress?: IHomeAddress;
    invoiceAddress?: IInvoiceAddress;
}

interface IPatientDetails {
    // TODO: CS-14503 refactor zip/zipCode to postalCode
    city: string;
    country: string;
    emailAddress: string;
    houseNumber: string;
    houseNumberSuffix?: string;
    street: string;
    zip: string;
}

interface IPatientInvoiceAddress {
    address: {
        city: string;
        country: AddressCountry;
        houseNumber: string;
        houseNumberSuffix?: string;
        postalCode: string;
        state?: string | null;
        street: string;
    };
    addressId: string;
    addressType: AddressType;
}

export function useConstInfoAndConsentModalState(patientId: string, onSubmit: (consent: boolean) => void) {
    const [patientInformation, setPatientInformation] = useState<IPatientInformation>();
    const [hasCostConsentPatient, setHasCostConsentPatient] = useState(false);
    const [paymentMethod, setPaymentMethod] = useState(PaymentMethod.ideal);
    const [validationErrors, setValidationErrors] = useState<ValidationErrors>(emptyValidationErrors);
    const abortableFetch = useAbortableFetch();

    useEffect(() => {
        getPatientDetails();
    }, [patientId]);

    function handleBlur(
        value: string | boolean | PaymentMethod | IHomeAddress | IInvoiceAddress,
        name: string,
        validateName?: string
    ) {
        const newState = {
            hasCostConsentPatient,
            [name]: value,
            patientInformation,
            paymentMethod,
        };
        const nameForValidation = validateName || name;
        validateOne(costInfoAndConsentSchema, newState, nameForValidation, validationErrors, setValidationErrors);
    }

    function handleChange(value: string | boolean | PaymentMethod | IHomeAddress | IInvoiceAddress, name: string) {
        switch (name) {
            case 'hasCostConsentPatient': {
                setHasCostConsentPatient(value as boolean);
                break;
            }
            case 'paymentMethod': {
                setPaymentMethod(value as PaymentMethod);
                break;
            }
            default: {
                setPatientInformation({
                    ...patientInformation,
                    [name]: value,
                });
            }
        }
    }

    async function getPatientDetails() {
        const patientUrl = `/api/patients/${patientId}`;
        const patientInvoiceAddressUrl = `${patientUrl}/address?addressType=invoice`;
        const [patientDetails, patientInvoiceAddress] = await Promise.all([
            (await abortableFetch.getJson(patientUrl)).json as IPatientDetails,
            (await abortableFetch.getJson(patientInvoiceAddressUrl)).json
                .patientAddress as IPatientInvoiceAddress | null,
        ]);

        setPatientInformation({
            emailAddress: patientDetails.emailAddress,
            homeAddress: {
                houseNumber: patientDetails.houseNumber,
                houseNumberSuffix: patientDetails.houseNumberSuffix,
                postalCode: patientDetails.zip,
            },
            invoiceAddress: prefillInvoiceAddress(patientDetails, patientInvoiceAddress),
        });
    }

    function prefillInvoiceAddress(
        patientDetailsWithHomeAddress: IPatientDetails,
        invoiceAddress: IPatientInvoiceAddress | null
    ) {
        if (invoiceAddress) {
            const { address } = invoiceAddress;

            return {
                ...address,
                country: AddressCountry.NL,
            };
        }

        return {
            city: patientDetailsWithHomeAddress.city,
            country: AddressCountry.NL,
            houseNumber: patientDetailsWithHomeAddress.houseNumber,
            houseNumberSuffix: patientDetailsWithHomeAddress.houseNumberSuffix,
            postalCode: patientDetailsWithHomeAddress.zip,
            street: patientDetailsWithHomeAddress.street,
        };
    }

    function getCostInfoAndConsentDataForValidation() {
        if (paymentMethod === PaymentMethod.ideal) {
            return {
                emailAddress: patientInformation?.emailAddress,
                hasCostConsentPatient,
                homeAddress: patientInformation?.homeAddress,
                paymentMethod,
            };
        } else {
            return { hasCostConsentPatient, invoiceAddress: patientInformation?.invoiceAddress, paymentMethod };
        }
    }

    async function submit() {
        const url = `/api/patients/${patientId}/savePaymentPreference`;
        const { item: body, errors } = validate(costInfoAndConsentSchema, getCostInfoAndConsentDataForValidation());

        if (!body) {
            setValidationErrors(errors);
        } else {
            await abortableFetch.postJson(url, body);
            onSubmit(hasCostConsentPatient);
        }
    }

    return {
        getCostInfoAndConsentDataForValidation,
        handleBlur,
        handleChange,
        state: {
            hasCostConsentPatient,
            paymentMethod,
            ...patientInformation,
        },
        submit,
        validationErrors,
    };
}
